import { useMemo } from 'react';
import { useLanguage } from '../theme/PreferencesContext';
import { translations } from './translations';
import type { TranslationKey } from './translations';

/**
 * Translator returned by useT(). Two call shapes:
 *
 *   t('settings.language')            — a known key, checked by the compiler.
 *   t(`nav.${id}`, item.label)        — a key computed at runtime, which the
 *                                       compiler cannot check, so an explicit
 *                                       English fallback is required.
 *
 * The second shape is what lets the sidebar and breadcrumb translate by route
 * id (see layout/Menu.tsx): a page with no `nav.<id>` entry yet renders the
 * English label already sitting in module-config, rather than a raw key.
 */
export interface Translator {
  (key: TranslationKey): string;
  (key: string, fallback: string): string;
}

// const t = useT(); t('settings.language')
//
// Mirrors mobileapp/src/i18n/useT.ts. A key missing from the active dictionary
// falls back to the supplied fallback, then to the key itself — never throws
// and never renders blank, which keeps a half-translated screen usable.
export const useT = (): Translator => {
  const { language } = useLanguage();

  return useMemo(() => {
    const dictionary = translations[language] as Record<string, string>;
    return ((key: string, fallback?: string) =>
      dictionary[key] ?? fallback ?? key) as Translator;
  }, [language]);
};
